import React, { useState, useEffect, useContext } from 'react';
import { QueueConfiguration, Case, SubjectImagePreferences, getCaseFeedback, SubjectJsonPreferences } from 'services';
import { StackItem, Stack, IStackStyles, getTheme } from '@fluentui/react';
import { ReviewContext } from './ReviewContextProvider';
import { ReviewCaseCardGrid } from './ReviewCaseCardGrid';
import { CaseContent, ZoomModal, CommandBar } from 'components/shared';

const singleContentPadding = { paddingLeft: 56 };

export interface ReviewContentProps {
  config: QueueConfiguration;
  cases: Case[];
  onTimerExpired?: () => void;
}

export const ReviewContent: React.FunctionComponent<ReviewContentProps> = props => {
  const defaultPreferences: SubjectImagePreferences & SubjectJsonPreferences = {
    ...props.config.uxPreferences.subjectImage,
    ...props.config.uxPreferences.subjectJson
  };

  const { state, dispatch } = useContext(ReviewContext);
  const [filters, setFilters] = useState<SubjectImagePreferences & SubjectJsonPreferences>(defaultPreferences);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [zoomItem, setZoomItem] = useState<Case | undefined>(undefined);
  const [caseCard, setCaseCard] = useState<number | undefined>(undefined);
  const [zoomInIsChecked, setZoomInIsChecked] = useState<boolean | undefined>(false);
  const theme = getTheme();
  const stackStyles: IStackStyles = {
    root: {
      background: theme.palette.neutralLighterAlt
    }
  };
  const singleContentCardStyle = {
    background: theme.palette.neutralPrimaryAlt
  };

  const cases = props.cases;

  useEffect(() => {
    setFilters(defaultPreferences);
    setTimeLeft(props.config.caseAssignedLifetimeInSeconds);
  }, [
    dispatch,
    props.config.uxPreferences.subjectImage,
    props.config.uxPreferences.subjectJson,
    props.config.caseAssignedLifetimeInSeconds
  ]);

  useEffect(() => {
    dispatch({ type: 'SETUP_REVIEW', payload: { cases: cases, queue: props.config } });

    // When reviewing a single case, that case is always selected.
    if (cases.length === 1) {
      dispatch({ type: 'SELECT_CASE', payload: cases });
    }

    // Fetch case feedback
    cases.map(async item => {
      const feedback = await getCaseFeedback(
        item.tenant.tenantId,
        item.title.titleId,
        item.subject.subjectType,
        item.subject.subjectId,
        item.id
      );
      dispatch({ type: 'SET_CASE_FEEDBACK', caseId: item.id, payload: feedback });
    });
  }, [cases, dispatch]);

  // Countdown timer value
  const onTimerExpired = props.onTimerExpired;
  const caseAssignedLifetimeInSeconds = props.config.caseAssignedLifetimeInSeconds;
  useEffect(() => {
    const initialTime = new Date().getTime();
    const decrementAndCallback = (count: number): number => {
      const delta = (new Date().getTime() - initialTime) / 1000;
      const nextValue = Math.floor(Math.max(caseAssignedLifetimeInSeconds - delta, 0));
      if (nextValue === 0 && count !== nextValue) {
        onTimerExpired && onTimerExpired();
      }
      dispatch({ type: 'SET_TIME_ELAPSED', payload: delta });
      return nextValue;
    };

    const timer = setInterval(() => {
      setTimeLeft(decrementAndCallback);
    }, 1000);
    return (): void => clearInterval(timer);
  }, [onTimerExpired, caseAssignedLifetimeInSeconds]);

  return (
    <Stack className="h-100 overflow-hidden position-relative" styles={stackStyles}>
      <ZoomModal
        config={props.config}
        case={zoomItem}
        caseCard={caseCard}
        isOpen={zoomItem !== undefined}
        onDismiss={(): void => {
          setZoomItem(undefined);
          setCaseCard(undefined);
        }}
        isChecked={zoomInIsChecked}
        setIsChecked={setZoomInIsChecked}
        timeLeftInSeconds={timeLeft}
        settings={filters}
        showCheckbox={true}
      />
      <StackItem>
        <CommandBar
          contentType={props.config.contentType}
          showSelectAll={state.cases.length !== 1}
          selectAllValue={state.cases.every(item => item?.isSelected || item?.opinion !== undefined)}
          subjectPreferences={filters}
          onSelectAllClick={(checked: boolean): void => {
            if (checked) {
              dispatch({ type: 'SELECT_ALL' });
            } else {
              dispatch({ type: 'UNSELECT_ALL' });
            }
          }}
          onFilterValueChange={setFilters}
          timeLeftInSeconds={timeLeft}
        />
      </StackItem>
      <StackItem className="position-relative h-100">
        <div className="h-100 w-100 overflow-auto position-absolute">
          {props.config.defaultMaxAssignedCaseCount === 1 && state.cases.length === 1 ? (
            <div className="h-100 w-100 pr-3 pb-4" style={singleContentPadding}>
              <div className="h-100 w-100" style={singleContentCardStyle}>
                <CaseContent case={state.cases[0].case} oneCase={true} config={props.config} settings={filters} />
              </div>
            </div>
          ) : (
            <ReviewCaseCardGrid
              config={props.config}
              settings={filters}
              onZoomClick={(item: Case, num?: number): void => {
                setZoomItem(item);
                num && setCaseCard(num);
              }}
              setZoomInIsChecked={setZoomInIsChecked}
              className="pl-5"
            />
          )}
        </div>
      </StackItem>
    </Stack>
  );
};
